import {
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  serverTimestamp,
  runTransaction,
  onSnapshot,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import {
  COLLECTIONS,
  VALIDATION,
  LOG_TYPES,
  HYDRATION_INTERVALS,
} from '../config/constants';
import { AppError, FirestoreError } from '../lib/errors';

/**
 * Servicio de "grupos de hidratación" (hydrationGroups).
 *
 * Dominio paralelo a feedingGroups pero sin stock, sin dispensers, sin
 * ciclos. Cada grupo representa un "balde compartido" — N mascotas
 * (puede ser 1) toman del mismo recipiente y el evento "Dar agua" se
 * registra una sola vez para todas.
 *
 * Schema mínimo: name + hydrationIntervalHours (6/12/24/48) + lastWateredAt/By.
 * Status (verde/amarillo/rojo) se deriva en client con useHydrationStatus.
 */

// =====================================================================
// VALIDADORES
// =====================================================================

const validateGroupName = (name) => {
  if (!name?.trim()) {
    throw new AppError('El nombre del balde es requerido', 'INVALID_NAME');
  }
  if (name.length > VALIDATION.HYDRATION_GROUP_NAME_MAX) {
    throw new AppError(
      `El nombre supera los ${VALIDATION.HYDRATION_GROUP_NAME_MAX} caracteres`,
      'INVALID_NAME'
    );
  }
};

const validateInterval = (hours) => {
  if (!HYDRATION_INTERVALS.includes(hours)) {
    throw new AppError(
      `La frecuencia debe ser una de: ${HYDRATION_INTERVALS.join(', ')} horas`,
      'INVALID_HYDRATION_INTERVAL'
    );
  }
};

// =====================================================================
// CRUD
// =====================================================================

/**
 * Crea un grupo de hidratación para un hogar.
 * Loguea NOTE con reason='hydration_group_created' para que aparezca en
 * la bitácora global. El log NO va a la bitácora de ninguna pet (es a
 * nivel grupo y todavía no hay pets asociadas).
 */
export const createHydrationGroup = async ({
  uid,
  familyId,
  name,
  hydrationIntervalHours,
}) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!familyId) throw new AppError('Hogar requerido', 'INVALID_INPUT');

  validateGroupName(name);
  validateInterval(hydrationIntervalHours);

  try {
    const groupsRef = collection(db, COLLECTIONS.HYDRATION_GROUPS);
    const docRef = await addDoc(groupsRef, {
      familyId,
      name: name.trim(),
      hydrationIntervalHours,
      lastWateredAt: null,
      lastWateredBy: null,
      lastPushSentAt: null,
      deletedAt: null,
      createdBy: uid,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });

    // Log a nivel de grupo. Pet=null porque es un evento del balde, no de
    // una mascota específica. hydrationGroupId apunta al recién creado.
    await addDoc(collection(db, COLLECTIONS.LOGS), {
      familyId,
      feedingGroupId: null,
      hydrationGroupId: docRef.id,
      petId: null,
      userId: uid,
      type: LOG_TYPES.NOTE,
      timestamp: serverTimestamp(),
      payload: {
        reason: 'hydration_group_created',
        groupName: name.trim(),
        intervalHours: hydrationIntervalHours,
      },
    });

    return { groupId: docRef.id };
  } catch (err) {
    if (err instanceof AppError) throw err;
    throw new FirestoreError('No se pudo crear el balde', err);
  }
};

/**
 * Actualiza name y/o hydrationIntervalHours.
 * Loguea NOTE específico según qué cambió (puede loguear ambos si cambian
 * los dos en la misma update).
 */
export const updateHydrationGroup = async ({ uid, groupId, updates }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!groupId) throw new AppError('Balde requerido', 'INVALID_INPUT');

  const ALLOWED = ['name', 'hydrationIntervalHours'];
  const filtered = {};
  for (const key of Object.keys(updates || {})) {
    if (ALLOWED.includes(key)) {
      filtered[key] = updates[key];
    } else {
      throw new AppError(`Campo no editable: ${key}`, 'INVALID_FIELD');
    }
  }
  if (Object.keys(filtered).length === 0) {
    throw new AppError('No hay cambios para aplicar', 'NO_CHANGES');
  }

  if ('name' in filtered) {
    validateGroupName(filtered.name);
    filtered.name = filtered.name.trim();
  }
  if ('hydrationIntervalHours' in filtered) {
    validateInterval(filtered.hydrationIntervalHours);
  }

  try {
    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.HYDRATION_GROUPS, groupId);
      const snap = await transaction.get(groupRef);
      if (!snap.exists()) {
        throw new AppError('El balde no existe', 'GROUP_NOT_FOUND');
      }
      const current = snap.data();
      if (current.deletedAt) {
        throw new AppError('El balde fue eliminado', 'GROUP_DELETED');
      }

      transaction.update(groupRef, {
        ...filtered,
        updatedAt: serverTimestamp(),
      });

      if ('name' in filtered && filtered.name !== current.name) {
        const logRef = doc(collection(db, COLLECTIONS.LOGS));
        transaction.set(logRef, {
          familyId: current.familyId,
          feedingGroupId: null,
          hydrationGroupId: groupId,
          petId: null,
          userId: uid,
          type: LOG_TYPES.NOTE,
          timestamp: serverTimestamp(),
          payload: {
            reason: 'hydration_group_renamed',
            previousName: current.name,
            newName: filtered.name,
          },
        });
      }
      if (
        'hydrationIntervalHours' in filtered &&
        filtered.hydrationIntervalHours !== current.hydrationIntervalHours
      ) {
        const logRef = doc(collection(db, COLLECTIONS.LOGS));
        transaction.set(logRef, {
          familyId: current.familyId,
          feedingGroupId: null,
          hydrationGroupId: groupId,
          petId: null,
          userId: uid,
          type: LOG_TYPES.NOTE,
          timestamp: serverTimestamp(),
          payload: {
            reason: 'hydration_group_interval_changed',
            groupName: filtered.name ?? current.name,
            previousInterval: current.hydrationIntervalHours,
            newInterval: filtered.hydrationIntervalHours,
          },
        });
      }
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo actualizar el balde', err);
  }
};

/**
 * Soft-delete del balde. Bloqueado si hay pets activas (no soft-deleted)
 * que apunten a este grupo via `pet.hydrationGroupId`. El usuario debe
 * desactivar la hidratación en cada pet primero (o moverlas a otro balde).
 */
export const softDeleteHydrationGroup = async ({ uid, groupId }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!groupId) throw new AppError('Balde requerido', 'INVALID_INPUT');

  try {
    // Pre-check fuera de la transacción: getDocs no se puede llamar adentro.
    // Acepta race acotada (si una pet se agrega entre check y commit, queda
    // referenciando un grupo soft-deleted; el UI maneja eso como "balde
    // inactivo").
    const petsRef = collection(db, COLLECTIONS.PETS);
    const q = query(petsRef, where('hydrationGroupId', '==', groupId));
    const snap = await getDocs(q);
    const activePets = snap.docs.filter((d) => !d.data().deletedAt);
    if (activePets.length > 0) {
      throw new AppError(
        `El balde tiene ${activePets.length} mascota${activePets.length === 1 ? '' : 's'} activa${activePets.length === 1 ? '' : 's'}. Desactivá la hidratación o moveelas a otro balde primero.`,
        'HYDRATION_GROUP_HAS_ACTIVE_PETS'
      );
    }

    await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.HYDRATION_GROUPS, groupId);
      const groupSnap = await transaction.get(groupRef);
      if (!groupSnap.exists()) {
        throw new AppError('El balde no existe', 'GROUP_NOT_FOUND');
      }
      const group = groupSnap.data();
      if (group.deletedAt) {
        throw new AppError('El balde ya fue eliminado', 'ALREADY_DELETED');
      }

      transaction.update(groupRef, {
        deletedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId: null,
        hydrationGroupId: groupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.NOTE,
        timestamp: serverTimestamp(),
        payload: {
          reason: 'hydration_group_deleted',
          groupName: group.name,
        },
      });
    });
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo eliminar el balde', err);
  }
};

// =====================================================================
// READS
// =====================================================================

export const fetchHydrationGroup = async (groupId) => {
  if (!groupId) throw new AppError('Balde requerido', 'INVALID_INPUT');
  try {
    const snap = await getDoc(doc(db, COLLECTIONS.HYDRATION_GROUPS, groupId));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (err) {
    throw new FirestoreError('No se pudo cargar el balde', err);
  }
};

export const subscribeToHydrationGroups = (familyId, onNext, onError) => {
  if (!familyId) {
    onError?.(new AppError('familyId requerido', 'INVALID_INPUT'));
    return () => {};
  }
  const groupsRef = collection(db, COLLECTIONS.HYDRATION_GROUPS);
  const q = query(groupsRef, where('familyId', '==', familyId));

  // Estrategia anti-cache-stale (bug histórico al restaurar el tab):
  // 1. Fetch INMEDIATO del server en paralelo. Garantiza data fresca aunque
  //    el cache local devuelva vacío (colección nueva sin pre-cache).
  // 2. onSnapshot para reactividad. Si recibe un snapshot fromCache con
  //    MENOS items que el último del server, lo ignora (es cache stale).
  //
  // El filtro "ignorar cache con menos items que server" cubre el caso
  // típico donde Firestore tiene cache local = [] pero el server tiene 1+
  // baldes. Sin este filtro, el cache snapshot pisaría al server snapshot
  // y la UI quedaría vacía.
  let lastServerCount = null;

  const emit = (docs, source, fromCache) => {
    const groups = docs
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((g) => !g.deletedAt);
    console.info(
      `[hydrationGroupService] ${source} — count: ${groups.length} fromCache: ${fromCache}`
    );
    // Anti-stale: si tenemos data del server con N items, no aceptar un
    // snapshot del cache con menos items.
    if (
      fromCache &&
      lastServerCount !== null &&
      groups.length < lastServerCount
    ) {
      console.info(
        '[hydrationGroupService] ignorando cache stale (count',
        groups.length,
        '< server',
        lastServerCount,
        ')'
      );
      return;
    }
    if (!fromCache) {
      lastServerCount = groups.length;
    }
    onNext(groups);
  };

  // Fetch inicial del SERVER en paralelo con el onSnapshot.
  (async () => {
    try {
      const { getDocsFromServer } = await import('firebase/firestore');
      const fresh = await getDocsFromServer(q);
      emit(fresh.docs, 'initial server fetch', false);
    } catch (err) {
      console.warn(
        '[hydrationGroupService] initial server fetch error (no bloqueante):',
        err?.message || err
      );
    }
  })();

  // onSnapshot para escuchar cambios futuros (otra pet del hogar crea un
  // balde, lo edita, etc.). includeMetadataChanges para saber si viene del
  // cache.
  return onSnapshot(
    q,
    { includeMetadataChanges: true },
    (snapshot) => emit(snapshot.docs, 'onSnapshot', snapshot.metadata.fromCache),
    (err) => {
      console.error('[hydrationGroupService] subscribe error:', err);
      onError?.(new FirestoreError('No se pudieron cargar los baldes', err));
    }
  );
};

// =====================================================================
// OPERACIÓN PRINCIPAL: dar agua
// =====================================================================

/**
 * Registra que las mascotas del balde tomaron agua. Transaccional:
 * actualiza lastWateredAt/By del grupo y escribe UN log de tipo WATERING
 * con snapshot de las pets afectadas.
 *
 * `affectedPets` es input (no se resuelve en transacción) porque Firestore
 * no permite getDocs dentro de runTransaction. El caller (context o UI) lo
 * pre-fetchea desde PetsContext con `pets.filter(p => p.hydrationGroupId === groupId)`.
 * Race acotada aceptable: si se suma una pet al balde entre el pre-fetch y
 * el commit, no participa de ESTE evento pero sí del próximo.
 *
 * @param {object} params
 * @param {string} params.uid
 * @param {string} params.groupId
 * @param {Array<{id: string, name: string}>} params.affectedPets - snapshot inmutable.
 *
 * @returns {object} { affectedCount, affectedNames }
 */
export const waterHydrationGroup = async ({ uid, groupId, affectedPets }) => {
  if (!uid) throw new AppError('Usuario requerido', 'INVALID_INPUT');
  if (!groupId) throw new AppError('Balde requerido', 'INVALID_INPUT');
  if (!Array.isArray(affectedPets) || affectedPets.length === 0) {
    throw new AppError(
      'No hay mascotas asignadas a este balde',
      'NO_PETS_IN_GROUP'
    );
  }
  // Sanity: cada pet debe tener id + name.
  for (const p of affectedPets) {
    if (!p?.id || !p?.name) {
      throw new AppError(
        'Mascotas afectadas con formato inválido',
        'INVALID_AFFECTED_PETS'
      );
    }
  }

  try {
    const result = await runTransaction(db, async (transaction) => {
      const groupRef = doc(db, COLLECTIONS.HYDRATION_GROUPS, groupId);
      const snap = await transaction.get(groupRef);
      if (!snap.exists()) {
        throw new AppError('El balde no existe', 'GROUP_NOT_FOUND');
      }
      const group = snap.data();
      if (group.deletedAt) {
        throw new AppError('El balde fue eliminado', 'GROUP_DELETED');
      }

      transaction.update(groupRef, {
        lastWateredAt: serverTimestamp(),
        lastWateredBy: uid,
        updatedAt: serverTimestamp(),
      });

      const logRef = doc(collection(db, COLLECTIONS.LOGS));
      transaction.set(logRef, {
        familyId: group.familyId,
        feedingGroupId: null,
        hydrationGroupId: groupId,
        petId: null,
        userId: uid,
        type: LOG_TYPES.WATERING,
        timestamp: serverTimestamp(),
        payload: {
          // Snapshot inmutable: si la pet se elimina o cambia de nombre
          // después, el log mantiene el dato original para la bitácora.
          affectedPetIds: affectedPets.map((p) => p.id),
          affectedPetNames: affectedPets.map((p) => p.name),
          intervalHours: group.hydrationIntervalHours,
        },
      });

      return {
        affectedCount: affectedPets.length,
        affectedNames: affectedPets.map((p) => p.name),
      };
    });

    return result;
  } catch (err) {
    if (err instanceof AppError) throw err;
    if (err?.cause instanceof AppError) throw err.cause;
    throw new FirestoreError('No se pudo registrar la toma de agua', err);
  }
};
